import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '../components/ui/dialog';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { 
  MapPin, 
  Plus, 
  Building2, 
  Car, 
  Trees,
  Edit,
  Trash2,
  RefreshCw,
  Plug,
  X,
  Layers,
  Search,
  Link2,
  Unlink,
  QrCode,
  Zap,
  FileText,
  Hash,
  AlertTriangle,
  Camera,
  Upload
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Html5QrcodeScanner } from 'html5-qrcode';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LOCATION_TYPES = {
  bina: { label: 'Bina', icon: Building2, color: 'bg-blue-500' },
  otopark: { label: 'Otopark', icon: Car, color: 'bg-purple-500' },
  acik_alan: { label: 'Açık Alan', icon: Trees, color: 'bg-green-500' }
};

const LocationsPage = () => {
  const { user, isYardimci, getEffectiveRole, getEffectiveSiteId, isSimulationActive } = useAuth();
  const [locations, setLocations] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState('all');
  
  // Simülasyon kontrolü
  const effectiveRole = getEffectiveRole ? getEffectiveRole() : user?.role;
  const effectiveSiteId = getEffectiveSiteId ? getEffectiveSiteId() : user?.site_id;
  const isEffectiveYardimci = effectiveRole === 'yardimci' || (isYardimci && isYardimci());
  const isEffectiveHesapYoneticisi = effectiveRole === 'hesap_yoneticisi' || effectiveRole === 'site_manager';
  
  // Sadece Super Admin ve Admin site seçebilir (simülasyon dahil)
  const isSuperOrAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const isEffectiveSuperOrAdmin = ['super_admin', 'admin'].includes(effectiveRole);
  
  // Yardımcı için düzenleme, silme, istasyon bağlama butonlarını gizle
  const canEdit = !isEffectiveYardimci;
  const canSeeAllAccounts = isSuperOrAdmin && isEffectiveSuperOrAdmin && !isEffectiveYardimci && !isEffectiveHesapYoneticisi;
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    account_id: '',
    type: 'bina',
    floor_section: '',
    description: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Form için istasyon listesi
  const [availableStations, setAvailableStations] = useState([]);
  const [loadingStations, setLoadingStations] = useState(false);
  const [selectedStationInfo, setSelectedStationInfo] = useState(null);
  
  // İstasyon bağlama modal state'leri
  const [showStationModal, setShowStationModal] = useState(false);
  const [selectedLocationForStation, setSelectedLocationForStation] = useState(null);
  const [stationSearchQuery, setStationSearchQuery] = useState('');
  const [stationSearchResults, setStationSearchResults] = useState([]);
  const [searchingStations, setSearchingStations] = useState(false);
  const [linkedStations, setLinkedStations] = useState([]); // Bu lokasyona bağlı istasyonlar
  const [loadingLinkedStations, setLoadingLinkedStations] = useState(false);
  
  // Sekme state'leri için
  const [stationSearchTab, setStationSearchTab] = useState('qr'); // 'qr', 'name', 'serial'
  const [showQRScanner, setShowQRScanner] = useState(false);
  const qrScannerRef = useRef(null);
  const qrFileInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    // user henüz yüklenmediyse bekle
    if (!user) {
      setLoading(false);
      return;
    }
    
    try {
      const token = localStorage.getItem('hims_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch sites
      const sitesRes = await fetch(`${API_URL}/api/sites`, { headers });
      if (sitesRes.ok) {
        let sitesData = await sitesRes.json();
        // Hesap Yöneticisi simülasyonunda sadece kendi sitesini göster
        if (isEffectiveHesapYoneticisi && effectiveSiteId) {
          sitesData = (sitesData || []).filter(s => s.id === effectiveSiteId);
        }
        setSites(sitesData);
      }

      // Fetch locations
      let url = `${API_URL}/api/locations`;
      
      // Simülasyon dahil rol kontrolü
      const userIsSuperOrAdmin = user?.role === 'super_admin' || user?.role === 'admin';
      const canAccessAllSites = userIsSuperOrAdmin && isEffectiveSuperOrAdmin && !isEffectiveHesapYoneticisi;
      
      // Hesap Yöneticisi simülasyonunda sadece kendi sitesinin lokasyonları
      if (isEffectiveHesapYoneticisi && effectiveSiteId) {
        url += `?account_id=${effectiveSiteId}`;
      } else if (!canAccessAllSites && (user?.site_id || effectiveSiteId)) {
        url += `?account_id=${effectiveSiteId || user.site_id}`;
      } else if (selectedSite && selectedSite !== 'all') {
        url += `?account_id=${selectedSite}`;
      }
      
      const locRes = await fetch(url, { headers });
      if (locRes.ok) {
        let locData = await locRes.json();
        // Ekstra frontend filtresi - Hesap Yöneticisi için
        if (isEffectiveHesapYoneticisi && effectiveSiteId) {
          locData = (locData || []).filter(loc => loc.account_id === effectiveSiteId || loc.site_id === effectiveSiteId);
        }
        setLocations(locData);
      } else {
        console.error('Locations fetch failed:', locRes.status);
        toast.error('Lokasyonlar yüklenemedi');
      }
    } catch (error) {
      console.error('Data fetch error:', error);
      toast.error('Veriler yüklenemedi. Lütfen sayfayı yenileyin');
    } finally {
      setLoading(false);
    }
  }, [selectedSite, user, isEffectiveHesapYoneticisi, effectiveSiteId, isEffectiveSuperOrAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Form için site seçildiğinde istasyonları çek
  const fetchStationsForSite = async (siteId) => {
    if (!siteId) {
      setAvailableStations([]);
      setSelectedStationInfo(null);
      return;
    }
    
    setLoadingStations(true);
    try {
      const token = localStorage.getItem('hims_token');
      const res = await fetch(`${API_URL}/api/stations?account_id=${siteId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setAvailableStations(data || []);
      }
    } catch (error) {
      console.error('Station fetch error:', error);
    } finally {
      setLoadingStations(false);
    }
  };

  // İstasyon seçildiğinde bilgilerini göster
  const handleStationSelect = (stationId) => {
    setFormData(prev => ({ ...prev, station_id: stationId }));
    if (stationId) {
      const station = availableStations.find(s => s.id === stationId);
      if (station) {
        setSelectedStationInfo({
          name: station.name,
          brand: station.brand,
          model: station.model,
          serial_number: station.serial_number,
          ocpp_id: station.ocpp_id
        });
      }
    } else {
      setSelectedStationInfo(null);
    }
  };

  // İstasyon arama fonksiyonu
  const searchStations = async (query) => {
    if (!query || query.length < 2) {
      setStationSearchResults([]);
      return;
    }
    
    setSearchingStations(true);
    try {
      const token = localStorage.getItem('hims_token');
      const res = await fetch(`${API_URL}/api/stations/search?q=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setStationSearchResults(data.results || []);
      }
    } catch (error) {
      console.error('Station search error:', error);
      toast.error('İstasyon araması başarısız');
    } finally {
      setSearchingStations(false);
    }
  };

  // Lokasyona bağlı istasyonları getir
  const fetchLinkedStations = async (locationId) => {
    setLoadingLinkedStations(true);
    try {
      const token = localStorage.getItem('hims_token');
      const res = await fetch(`${API_URL}/api/stations?location_id=${locationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setLinkedStations(data);
      }
    } catch (error) {
      console.error('Linked stations fetch error:', error);
    } finally {
      setLoadingLinkedStations(false);
    }
  };

  // İstasyon bağlama modalını aç
  const openStationModal = async (location) => {
    setSelectedLocationForStation(location);
    setStationSearchQuery('');
    setStationSearchResults([]);
    setShowStationModal(true);
    await fetchLinkedStations(location.id);
  };

  // İstasyonu lokasyona bağla
  const linkStationToLocation = async (stationId) => {
    try {
      const token = localStorage.getItem('hims_token');
      const res = await fetch(`${API_URL}/api/stations/${stationId}`, {
        method: 'PATCH',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ location_id: selectedLocationForStation.id })
      });
      
      if (res.ok) {
        toast.success('İstasyon bu lokasyona bağlandı');
        await fetchLinkedStations(selectedLocationForStation.id);
        setStationSearchQuery('');
        setStationSearchResults([]);
        fetchData(); // Cihaz sayısını güncelle
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Bağlantı başarısız');
      }
    } catch (error) {
      console.error('Link station error:', error);
      toast.error('Bağlantı sırasında hata oluştu');
    }
  };

  // İstasyonu lokasyondan çıkar
  const unlinkStationFromLocation = async (stationId) => {
    try {
      const token = localStorage.getItem('hims_token');
      const res = await fetch(`${API_URL}/api/stations/${stationId}`, {
        method: 'PATCH',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ location_id: null })
      });
      
      if (res.ok) {
        toast.success('İstasyon bu lokasyondan çıkarıldı');
        await fetchLinkedStations(selectedLocationForStation.id);
        fetchData(); // Cihaz sayısını güncelle
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Çıkarma başarısız');
      }
    } catch (error) {
      console.error('Unlink station error:', error);
      toast.error('Çıkarma sırasında hata oluştu');
    }
  };

  // QR Tarama başlat
  const startQRScanner = () => {
    setShowQRScanner(true);
    
    setTimeout(() => {
      const config = {
        fps: 10,
        qrbox: { width: 220, height: 220 },
        aspectRatio: 1.0
      };
      
      const scanner = new Html5QrcodeScanner("location-qr-reader", config, false);
      qrScannerRef.current = scanner;
      
      scanner.render(
        (decodedText) => {
          handleQRCodeFound(decodedText);
          stopQRScanner();
        },
        (errorMessage) => {
          // Tarama devam ediyor
        }
      );
    }, 100);
  };

  // QR Tarama durdur
  const stopQRScanner = () => {
    if (qrScannerRef.current) {
      qrScannerRef.current.clear().catch(() => {});
      qrScannerRef.current = null;
    }
    setShowQRScanner(false);
  };

  // QR Kod bulunduğunda
  const handleQRCodeFound = async (qrCode) => {
    stopQRScanner();
    
    try {
      setSearchingStations(true);
      const token = localStorage.getItem('hims_token');
      
      const res = await fetch(`${API_URL}/api/stations/find-by-qr?qr_code=${encodeURIComponent(qrCode)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.station) {
          setStationSearchResults([{
            station: data.station,
            location: data.location || null
          }]);
          toast.success(`QR Kod ile bulundu: ${data.station.name}`);
        } else {
          toast.error('Bu QR koda ait istasyon bulunamadı');
          setStationSearchResults([]);
        }
      } else {
        toast.error('Bu QR koda ait istasyon bulunamadı');
        setStationSearchResults([]);
      }
    } catch (error) {
      console.error('QR search error:', error);
      toast.error('QR kod araması başarısız');
    } finally {
      setSearchingStations(false);
    }
  };

  // Dosyadan QR oku
  const handleQRFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const html5QrCode = new Html5Qrcode("temp-qr-reader");
      
      const result = await html5QrCode.scanFile(file, true);
      html5QrCode.clear();
      
      handleQRCodeFound(result);
    } catch (error) {
      console.error('QR file read error:', error);
      toast.error('QR kod okunamadı. Lütfen geçerli bir QR kod resmi seçin.');
    }
    
    // Input'u temizle
    if (qrFileInputRef.current) {
      qrFileInputRef.current.value = '';
    }
  };

  // Modal kapandığında QR scanner'ı temizle
  useEffect(() => {
    if (!showStationModal) {
      stopQRScanner();
      setStationSearchTab('qr');
      setStationSearchQuery('');
      setStationSearchResults([]);
    }
  }, [showStationModal]);

  // Arama debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (stationSearchQuery) {
        searchStations(stationSearchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [stationSearchQuery]);

  // Formda site seçimi değiştiğinde istasyonları çek
  useEffect(() => {
    if (formData.account_id && showModal) {
      fetchStationsForSite(formData.account_id);
    }
  }, [formData.account_id, showModal]);

  const handleOpenModal = (location = null) => {
    if (location) {
      setEditingLocation(location);
      setFormData({
        name: location.name || '',
        account_id: location.account_id || '',
        type: location.type || 'bina',
        floor_section: location.floor_section || '',
        description: location.description || ''
      });
      // Düzenleme modunda istasyonları çek
      if (location.account_id) {
        fetchStationsForSite(location.account_id);
      }
      // Mevcut istasyon bilgisini göster
      if (location.station_id && location.station_name) {
        setSelectedStationInfo({
          name: location.station_name,
          brand: location.station_brand,
          model: location.station_model,
          serial_number: location.station_serial_number,
          ocpp_id: location.station_ocpp_id
        });
      } else {
        setSelectedStationInfo(null);
      }
    } else {
      setEditingLocation(null);
      // Hesap yöneticisi ise otomatik kendi hesabını seç
      const defaultAccountId = !isSuperOrAdmin && user?.site_id 
        ? user.site_id 
        : (selectedSite !== 'all' ? selectedSite : '');
      
      setFormData({
        name: '',
        account_id: defaultAccountId,
        type: 'bina',
        floor_section: '',
        description: ''
      });
      setSelectedStationInfo(null);
      setAvailableStations([]);
      // Varsayılan site varsa istasyonları çek
      if (defaultAccountId) {
        fetchStationsForSite(defaultAccountId);
      }
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    // Hesap yöneticisi için account_id otomatik atanıyor, sadece ad kontrolü yap
    const accountId = !isSuperOrAdmin && user?.site_id ? user.site_id : formData.account_id;
    
    if (!formData.name) {
      toast.error('Lütfen lokasyon adı girin');
      return;
    }
    
    if (isSuperOrAdmin && !accountId) {
      toast.error('Lütfen hesap seçin');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('hims_token');
      const url = editingLocation 
        ? `${API_URL}/api/locations/${editingLocation.id}`
        : `${API_URL}/api/locations`;
      
      const method = editingLocation ? 'PATCH' : 'POST';
      
      const body = {
        name: formData.name,
        type: formData.type,
        floor_section: formData.floor_section || null,
        description: formData.description || null
      };

      if (!editingLocation) {
        body.account_id = accountId;
      }

      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (res.ok) {
        toast.success(editingLocation ? 'Lokasyon güncellendi' : 'Lokasyon oluşturuldu');
        setShowModal(false);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Lokasyon kaydedilemedi. Lütfen tekrar deneyin');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (locationId) => {
    if (!window.confirm('Bu lokasyonu silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const token = localStorage.getItem('hims_token');
      const res = await fetch(`${API_URL}/api/locations/${locationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Lokasyon silindi');
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Lokasyon silinemedi. Bağlı istasyonlar olabilir');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Sunucuya bağlanılamadı');
    }
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site?.name || 'Bilinmeyen Hesap';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MapPin className="h-6 w-6 text-primary" />
            Lokasyonlar
          </h1>
          <p className="text-muted-foreground">
            Hesap içi lokasyonları yönetin (Blok A, Otopark vb.)
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Site Filter - Sadece Super Admin ve Admin için, Yardımcı hariç */}
          {canSeeAllAccounts && (
            <Select value={selectedSite} onValueChange={setSelectedSite}>
              <SelectTrigger className="w-[200px]" data-testid="site-filter">
                <Building2 className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Hesap Filtrele" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Hesaplar</SelectItem>
                {sites.map((site) => (
                  <SelectItem key={site.id} value={site.id}>
                    {site.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {canEdit && (
            <Button onClick={() => handleOpenModal()} data-testid="add-location-btn">
              <Plus className="h-4 w-4 mr-2" />
              Yeni Lokasyon
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <MapPin className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Toplam</p>
                <p className="text-2xl font-bold">{locations.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Building2 className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Bina</p>
                <p className="text-2xl font-bold">{locations.filter(l => l.type === 'bina').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Car className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Otopark</p>
                <p className="text-2xl font-bold">{locations.filter(l => l.type === 'otopark').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <Trees className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Açık Alan</p>
                <p className="text-2xl font-bold">{locations.filter(l => l.type === 'acik_alan').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Locations Grid */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : locations.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <MapPin className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Henüz lokasyon eklenmemiş</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => handleOpenModal()}
            >
              <Plus className="h-4 w-4 mr-2" />
              İlk Lokasyonu Ekle
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {locations.map((location) => {
            const typeConfig = LOCATION_TYPES[location.type] || LOCATION_TYPES.bina;
            const TypeIcon = typeConfig.icon;
            
            return (
              <Card key={location.id} className="hover:border-primary/50 transition-colors overflow-hidden">
                {/* Lokasyon Tipi İkonu - Fotoğraf yerine büyük ikon */}
                <div className={`w-full h-32 flex items-center justify-center ${typeConfig.color}/10`}>
                  <TypeIcon className={`w-16 h-16 ${typeConfig.color.replace('bg-', 'text-')}`} />
                </div>
                
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${typeConfig.color}/20`}>
                        <TypeIcon className={`h-5 w-5 ${typeConfig.color.replace('bg-', 'text-')}`} />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{location.name}</CardTitle>
                        <p className="text-sm text-muted-foreground">
                          {getSiteName(location.account_id)}
                        </p>
                      </div>
                    </div>
                    <Badge className={`${typeConfig.color} text-white`}>
                      {typeConfig.label}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Kat/Bölüm */}
                  {location.floor_section && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                      <Layers className="h-4 w-4" />
                      <span>{location.floor_section}</span>
                    </div>
                  )}
                  
                  {/* Yol Tarifi */}
                  {location.description && (
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {location.description}
                    </p>
                  )}
                  
                  <div className="flex flex-wrap gap-2 mb-3">
                    <Badge 
                      variant="outline" 
                      className="text-xs cursor-pointer hover:bg-primary/10 transition-colors"
                      onClick={() => openStationModal(location)}
                    >
                      <Plug className="h-3 w-3 mr-1" />
                      {location.charger_count || 0} Cihaz
                      <Plus className="h-3 w-3 ml-1 text-primary" />
                    </Badge>
                  </div>
                  
                  {/* İstasyon Ekle Butonu - Yardımcı için gizli */}
                  {canEdit && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="w-full mb-3 text-primary border-primary/30 hover:bg-primary/10"
                      onClick={() => openStationModal(location)}
                    >
                      <Link2 className="h-4 w-4 mr-2" />
                      İstasyon Bağla
                    </Button>
                  )}
                  
                  <div className="flex items-center justify-between pt-2 border-t border-border">
                    <span className="text-xs text-muted-foreground">
                      {formatDate(location.created_at)}
                    </span>
                    {canEdit && (
                      <div className="flex gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleOpenModal(location)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="text-red-500 hover:text-red-600"
                          onClick={() => handleDelete(location.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingLocation ? 'Lokasyon Düzenle' : 'Yeni Lokasyon'}
            </DialogTitle>
            <DialogDescription>
              Hesap içi lokasyon bilgilerini girin
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            {/* Lokasyon Adı */}
            <div>
              <label className="text-sm font-medium mb-2 block">Lokasyon Adı *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="örn: Blok A, Kapalı Otopark"
                data-testid="location-name-input"
              />
            </div>
            
            {/* Hesap Seçimi - Sadece Super Admin/Admin için ve yeni oluştururken */}
            {!editingLocation && isSuperOrAdmin && (
              <div>
                <label className="text-sm font-medium mb-2 block">Hesap *</label>
                <Select 
                  value={formData.account_id} 
                  onValueChange={(val) => setFormData({ ...formData, account_id: val })}
                >
                  <SelectTrigger data-testid="location-account-select">
                    <SelectValue placeholder="Hesap seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map((site) => (
                      <SelectItem key={site.id} value={site.id}>
                        {site.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Hesap Yöneticisi için bilgi mesajı */}
            {!editingLocation && !isSuperOrAdmin && (
              <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                <p className="text-sm text-blue-600 dark:text-blue-400">
                  📍 Lokasyon otomatik olarak kendi hesabınıza eklenecektir
                </p>
              </div>
            )}
            
            {/* Lokasyon Tipi */}
            <div>
              <label className="text-sm font-medium mb-2 block">Lokasyon Tipi</label>
              <Select 
                value={formData.type} 
                onValueChange={(val) => setFormData({ ...formData, type: val })}
              >
                <SelectTrigger data-testid="location-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bina">Bina</SelectItem>
                  <SelectItem value="otopark">Otopark</SelectItem>
                  <SelectItem value="acik_alan">Açık Alan</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Kat/Bölüm */}
            <div>
              <label className="text-sm font-medium mb-2 block">Kat / Bölüm</label>
              <Input
                value={formData.floor_section}
                onChange={(e) => setFormData({ ...formData, floor_section: e.target.value })}
                placeholder="örn: Bodrum Kat, B2, 3. Kat"
              />
            </div>
            
            {/* Yol Tarifi */}
            <div>
              <label className="text-sm font-medium mb-2 block">Yol Tarifi</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="örn: Ana girişten sola dönün, asansörün yanında"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              data-testid="save-location-btn"
            >
              {saving && <RefreshCw className="h-4 w-4 animate-spin mr-2" />}
              {editingLocation ? 'Güncelle' : 'Oluştur'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İstasyon Bağlama Modalı */}
      <Dialog open={showStationModal} onOpenChange={setShowStationModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-primary" />
              İstasyon Bağla: {selectedLocationForStation?.name}
            </DialogTitle>
            <DialogDescription>
              QR Kod, İstasyon Adı veya Seri No ile istasyon arayın ve bu lokasyona bağlayın.
            </DialogDescription>
          </DialogHeader>
          
          {/* Lokasyon Bilgisi */}
          {selectedLocationForStation && (
            <div className="p-3 rounded-lg bg-muted/50 mb-4">
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4 text-primary" />
                <span className="font-medium">{selectedLocationForStation.name}</span>
                {selectedLocationForStation.floor_section && (
                  <Badge variant="outline" className="text-xs">
                    {selectedLocationForStation.floor_section}
                  </Badge>
                )}
              </div>
              {selectedLocationForStation.description && (
                <p className="text-xs text-muted-foreground mt-1 ml-6">
                  {selectedLocationForStation.description}
                </p>
              )}
            </div>
          )}

          {/* 3 Sekmeli Arama */}
          <div className="mb-4">
            {/* Sekme Başlıkları */}
            <div className="flex border-b border-border mb-4">
              <button
                onClick={() => { setStationSearchTab('qr'); setStationSearchResults([]); setStationSearchQuery(''); }}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  stationSearchTab === 'qr' 
                    ? 'border-primary text-primary' 
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid="tab-qr"
              >
                <QrCode className="h-4 w-4" />
                QR Kod
              </button>
              <button
                onClick={() => { setStationSearchTab('name'); setStationSearchResults([]); setStationSearchQuery(''); stopQRScanner(); }}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  stationSearchTab === 'name' 
                    ? 'border-primary text-primary' 
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid="tab-name"
              >
                <FileText className="h-4 w-4" />
                İstasyon Adı
              </button>
              <button
                onClick={() => { setStationSearchTab('serial'); setStationSearchResults([]); setStationSearchQuery(''); stopQRScanner(); }}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  stationSearchTab === 'serial' 
                    ? 'border-primary text-primary' 
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid="tab-serial"
              >
                <Hash className="h-4 w-4" />
                Seri No
              </button>
            </div>

            {/* Sekme İçerikleri */}
            {stationSearchTab === 'qr' && (
              <div className="space-y-4">
                {/* QR Tarama veya Dosya Yükleme */}
                {!showQRScanner ? (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button 
                      variant="outline" 
                      onClick={startQRScanner}
                      className="flex-1"
                      data-testid="btn-start-qr-camera"
                    >
                      <Camera className="h-4 w-4 mr-2" />
                      Kamera ile Tara
                    </Button>
                    <div className="flex-1">
                      <input
                        ref={qrFileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleQRFileUpload}
                        className="hidden"
                        id="qr-file-upload"
                      />
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={() => qrFileInputRef.current?.click()}
                        data-testid="btn-upload-qr-image"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Resim Yükle
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div id="location-qr-reader" className="w-full rounded-lg overflow-hidden border" />
                    <Button 
                      variant="outline" 
                      onClick={stopQRScanner}
                      className="w-full"
                      data-testid="btn-stop-qr-camera"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Taramayı Durdur
                    </Button>
                  </div>
                )}
                {/* Gizli temp container */}
                <div id="temp-qr-reader" style={{ display: 'none' }} />
              </div>
            )}

            {stationSearchTab === 'name' && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="İstasyon adı ile ara..."
                  value={stationSearchQuery}
                  onChange={(e) => setStationSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-by-name"
                  autoFocus
                />
                {searchingStations && (
                  <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </div>
            )}

            {stationSearchTab === 'serial' && (
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Seri no veya OCPP ID ile ara..."
                  value={stationSearchQuery}
                  onChange={(e) => setStationSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-by-serial"
                  autoFocus
                />
                {searchingStations && (
                  <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </div>
            )}
          </div>

          {/* Arama Sonuçları */}
          {stationSearchResults.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                <Search className="h-4 w-4" />
                Arama Sonuçları ({stationSearchResults.length})
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {stationSearchResults.map((result) => (
                  <div 
                    key={result.station.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Zap className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{result.station.name}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>OCPP: {result.station.ocpp_id}</span>
                          {result.station.qr_code && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <QrCode className="h-3 w-3" />
                                {result.station.qr_code}
                              </span>
                            </>
                          )}
                        </div>
                        {result.location && (
                          <div className="flex items-center gap-1 text-xs text-amber-600 mt-1">
                            <MapPin className="h-3 w-3" />
                            Mevcut: {result.location.name}
                          </div>
                        )}
                      </div>
                    </div>
                    <Button 
                      size="sm"
                      onClick={() => linkStationToLocation(result.station.id)}
                      disabled={result.station.location_id === selectedLocationForStation?.id}
                      data-testid={`link-station-${result.station.id}`}
                    >
                      {result.station.location_id === selectedLocationForStation?.id ? (
                        'Zaten Bağlı'
                      ) : (
                        <>
                          <Link2 className="h-4 w-4 mr-1" />
                          Bağla
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sonuç Bulunamadı Mesajı */}
          {stationSearchQuery.length >= 2 && stationSearchResults.length === 0 && !searchingStations && (stationSearchTab === 'name' || stationSearchTab === 'serial') && (
            <div className="mb-4 p-4 rounded-lg border border-orange-500/30 bg-orange-500/10 text-center">
              <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-orange-500" />
              <p className="font-medium text-orange-700 dark:text-orange-400">
                Bu istasyon sistemde kayıtlı değil
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                "{stationSearchQuery}" ile eşleşen istasyon bulunamadı.
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                İstasyonun QR kodunu tarayarak veya farklı bir arama terimi deneyerek tekrar arayın.
              </p>
            </div>
          )}

          {/* Mevcut Bağlı İstasyonlar */}
          <div>
            <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
              <Plug className="h-4 w-4 text-green-500" />
              Bu Lokasyona Bağlı İstasyonlar ({linkedStations.length})
            </h4>
            
            {loadingLinkedStations ? (
              <div className="flex items-center justify-center py-4">
                <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : linkedStations.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                <Plug className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Bu lokasyona henüz istasyon bağlanmamış</p>
                <p className="text-xs">Yukarıdaki arama kutusunu kullanarak istasyon ekleyin</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {linkedStations.map((station) => (
                  <div 
                    key={station.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-green-500/5 border-green-500/30"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-green-500/20">
                        <Zap className="h-4 w-4 text-green-500" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{station.name}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>OCPP: {station.ocpp_id}</span>
                          <span>•</span>
                          <span>{station.power_capacity} kW</span>
                          {station.qr_code && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <QrCode className="h-3 w-3" />
                                QR mevcut
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button 
                      size="sm"
                      variant="outline"
                      className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                      onClick={() => unlinkStationFromLocation(station.id)}
                      data-testid={`unlink-station-${station.id}`}
                    >
                      <Unlink className="h-4 w-4 mr-1" />
                      Çıkar
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStationModal(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LocationsPage;
